import { css, styled } from 'styled-components';
import { motion } from 'motion/react';
import Link from 'next/link';
import {
  FrostedGlassLight,
  BodySmReg,
  BodyMedBold,
  EyebrowXsBold,
} from '@seed-health/tokens';
import Button from 'components/library/Button';
import { mqLargeMax, mqLargeMin } from 'styled/tools/media-queries';
import { FontFamily } from 'styled/fonts/font-family';
import Label from 'components/library/Label';

const GlassTransition = css`
  transition:
    background-color 0.3s ease-in-out,
    color 0.3s ease-in-out,
    backdrop-filter 0.3s ease-in-out,
    -webkit-backdrop-filter 0.3s ease-in-out;
`;

const glassEffectDark = css`
  background: var(--scrim-glass-dark-35);
  backdrop-filter: ${FrostedGlassLight};
  -webkit-backdrop-filter: ${FrostedGlassLight};
`;

export const lightPill = css`
  background: var(--actions-inverse);
  color: var(--actions-primary);
`;

export const GlassPillContainer = styled.div`
  ${GlassTransition};
  border-radius: var(--radius-full);

  html.with-little-scrolled & {
    ${glassEffectDark}
  }
`;

// Green-theme colors read --nav-* so the homepage edge worker can switch an
// experiment variant to white-theme colors before hydration.
export const NavContainer = styled.div`
  align-items: center;
  border-radius: var(--radius-full);
  display: inline-flex;
  gap: 1em;
  min-height: 48px;
  overflow: hidden;
  padding-inline: var(--spacing-16);
  position: relative;
  width: 100%;

  &.is-open {
    padding-inline-end: 0;
  }

  @media ${mqLargeMin} {
    padding-inline: 2em;
  }

  html.with-little-scrolled & a {
    color: var(--fill-primary);
  }

  html:not(.with-little-scrolled) &.theme-green:not(.is-open) .seed-logo {
    color: var(--nav-logo, var(--fill-brand));
  }
`;

export const GlassContainer = styled(motion.div)`
  ${GlassTransition};
  border-radius: inherit;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  @media ${mqLargeMax} {
    inset: 0;
  }

  html.with-little-scrolled & {
    ${glassEffectDark}
  }

  &.is-open {
    ${glassEffectDark}
  }
`;

export const GlassContainerMask = styled.div`
  border-radius: var(--radius-full);
  overflow: hidden;
  position: absolute;
  inset: 0 var(--spacing-8);
  z-index: 1;
  display: flex;
  height: 100%;
`;

export const SiteHeaderCtas = styled(GlassPillContainer)`
  align-items: center;
  display: inline-flex;
  padding: 6px;
`;

// Pill height matches the parent SiteHeaderCtas glass-pill design
const NAV_PILL_HEIGHT_PX = 36;

export const CartButton = styled(Button)`
  height: ${NAV_PILL_HEIGHT_PX}px;
  margin-inline-end: var(--spacing-8);
  gap: 0;

  box-sizing: border-box;
  border: 3px solid transparent;

  html:not(.with-little-scrolled) &.theme-green:not(:active) {
    background: var(--nav-cta-background, var(--fill-brand));
    color: var(--nav-cta-ink, var(--fill-primary));
  }

  html.with-little-scrolled &:not(.theme-white) {
    ${glassEffectDark}
    color: var(--actions-inverse);
  }

  /* A white-nav route puts this pill over dark art, where it already rests light.
     Scrolling keeps it light rather than inverting it to glass — the same state
     the large nav shows. */
  html.with-little-scrolled &.theme-white {
    ${lightPill}
  }

  html.with-little-scrolled & svg {
    opacity: 0;
    display: none;
  }

  &.with-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
  }

  /* Light ink on hover would vanish against the light pill, so the white-nav
     variant keeps the ink its fill already sets. */
  &&:not(.theme-white):hover,
  &&:not(.theme-white):focus,
  &&:not(.theme-white) *:hover,
  &&:not(.theme-white) *:focus {
    color: var(--actions-inverse);
  }
`;

export const CartCount = styled.sup`
  color: inherit;
`;

export const TabButtonGroup = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding-right: var(--spacing-8);
  width: 100%;
  z-index: 5;
`;

export const TabButton = styled.button`
  background: none;
  border: none;
  color: var(--fill-primary);
  cursor: pointer;
  font-size: var(--font-size-350);
  padding: 0.5em 0.75em;
  position: relative;
  line-height: 1;

  html:not(.with-little-scrolled) &.theme-green {
    color: var(--nav-ink, var(--fill-brand));
  }

  &.with-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
  }
`;

export const Referral25Label = styled(Label)`
  padding: 3px 5px;
  --lh: 1.4;
`;

export const TabPill = styled(motion.div)`
  background: var(--scrim-glass-light-10);
  border-radius: var(--radius-full);
  height: 100%;
  position: absolute;
  bottom: 0;
  left: -0.25em;
  right: -0.25em;
`;

export const NavButton = styled(motion.button)`
  ${GlassTransition};
  background: transparent;
  border: none;
  border-radius: 99em;
  color: var(--fill-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${NAV_PILL_HEIGHT_PX}px;
  padding-inline: 12px;
  position: relative;

  html.with-little-scrolled &:not(.is-open) {
    ${glassEffectDark}
  }

  .bar {
    stroke: var(--fill-primary);
    transform-origin: 50% 50%;
    transition: transform 0.15s cubic-bezier(0.75, 0, 0.25, 1);
  }

  html:not(.with-little-scrolled) &.theme-green:not(.is-open) .bar {
    stroke: var(--nav-ink, var(--fill-brand));
  }

  &.is-open {
    .bar1 {
      transform: rotate(45deg) translateY(3px);
      stroke-width: 2.5px;
    }

    .bar2 {
      transform: rotate(-45deg) translateY(-3px);
      stroke-width: 2.5px;
    }
  }
`;

interface NavDropdownProps {
  alignment?: 'left' | 'right' | 'full';
}

export const NavDropdown = styled(motion.div)<NavDropdownProps>`
  border-radius: var(--radius-xl);
  ${glassEffectDark}
  display: flex;
  flex-direction: column;
  left: ${({ alignment }) =>
    alignment === 'right' ? 'auto' : 'var(--spacing-8)'};
  padding: var(--spacing-16);
  position: absolute;
  right: 0;
  top: calc(100% + var(--spacing-8));
  width: min(calc(100vw - calc(var(--spacing-8) * 2)), 375px);
  max-height: calc(100vh - 144px);
  z-index: 90;

  @media ${mqLargeMax} {
    width: calc(100vw - calc(var(--spacing-8) * 2));
    max-height: calc(100vh - 192px);
  }

  @media ${mqLargeMin} {
    left: ${({ alignment }) => (alignment === 'right' ? 'auto' : '0')};
  }
`;

NavDropdown.defaultProps = {
  alignment: 'full',
};

/* Scrolling is off while the sections animate, so their transforms can't flash a
   scrollbar; the caller turns it on once they've settled. */
export const NavDropdownScrollArea = styled.div<{ $scrollable: boolean }>`
  flex: 1;
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'hidden')};
  overscroll-behavior: contain;
`;

export const NavDropdownStickyLink = styled(motion.div)`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  /* No horizontal padding — the link right-aligns to the content edge per the design. */
  padding: var(--spacing-16) 0 var(--spacing-8);
`;

export const NavDropdownContent = styled(NavDropdown)`
  width: 375px;
`;

export const NavListHeading = styled.p<{ $account?: boolean }>`
  align-items: center;
  display: flex;
  gap: var(--spacing-8);
  text-transform: uppercase;

  ${({ $account }) =>
    $account
      ? css`
          ${EyebrowXsBold}
          color: var(--text-inverse-secondary);
          padding: var(--spacing-8);
        `
      : css`
          color: var(--text-inverse-primary);
          opacity: var(--state-layer-dark-disabled);
          font-size: var(--font-size-300);
          font-weight: 500;
          letter-spacing: 0.02em;
          margin-block-end: var(--spacing-16);
        `}
`;

export const TopNav = styled.nav`
  display: flex;
  font-family: var(--ff, ${FontFamily.sansMedium});
  justify-content: space-between;
  min-height: 48px;
  @media ${mqLargeMin} {
    min-height: 56px;
  }
`;

export const SiteHeader = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
`;

export const SiteHeaderNav = styled.div`
  display: flex;
  position: relative;
  max-width: 375px;
  width: 100%;
`;

export const NavSection = styled(motion.div)`
  & + & {
    margin-top: 1em;
  }

  + * {
    margin-block-start: var(--spacing-16);
  }
`;

export const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  padding-inline-start: 0;
  width: 100%;
`;

export const NavListItem = styled.li`
  border-radius: 24px;
  padding: var(--spacing-8);
  transition: background-color 0.2s ease-in-out;

  @media (hover: hover) {
    &:hover {
      background-color: var(--scrim-glass-light-10);
    }
  }
`;

export const NavLink = styled(Link)`
  align-items: center;
  color: var(--fill-primary);
  display: grid;
  gap: var(--spacing-16);
  grid-template-columns: auto 1fr;
  text-decoration: none;

  && {
    color: var(--fill-primary);
  }

  && p:hover,
  && sup:hover {
    color: var(--fill-primary);
  }
`;

/* Glass tile behind a transparent product cutout, sized to match the photo
   thumbnails on neighbouring items. */
export const NavMediaTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 75px;
  height: 75px;
  padding: var(--spacing-4);
  border-radius: var(--radius-md);
  background-color: var(--scrim-glass-light-10);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const NavLinkCopy = styled.div<{ $stacked?: boolean }>`
  ${({ $stacked }) =>
    $stacked &&
    css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-8);
    `}
`;

export const ExtraLinkList = styled.ul<{ $account?: boolean }>`
  ${({ $account }) =>
    $account &&
    css`
      a {
        ${BodyMedBold}
      }
    `}
`;

export const NavLinkTitle = styled.p<{ $account?: boolean }>`
  margin-block-start: 0;

  ${({ $account }) =>
    $account
      ? css`
          ${BodyMedBold}
          color: var(--text-inverse-primary);
        `
      : css`
          color: var(--fill-primary);
          font-size: var(--font-size-400);
          font-weight: 500;
        `}
`;

export const LabsTitle = styled.p`
  margin-left: -6px;
  color: var(--fill-primary);
  font-size: var(--font-size-400);
  font-weight: 500;
  margin-block-start: 0;

  &:before {
    content: '【';
    left: -44px;
  }

  &:after {
    content: '】';
    right: 60px;
    transition: all 0.5s linear;
  }
`;

export const NavLinkText = styled.p<{ $account?: boolean }>`
  sup {
    top: -3px;
  }

  ${({ $account }) =>
    $account
      ? css`
          ${BodySmReg}
          color: var(--text-inverse-secondary);
        `
      : css`
          color: var(--fill-primary);
          font-size: var(--font-size-350);
          font-weight: 350;
          opacity: 0.6;
        `}
`;

export const tabVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  },
};

export const tabVariantsMobile = {
  hidden: {
    opacity: 0,

    transition: {
      duration: 0.1,
      ease: 'easeOut',
    },
  },
  visible: {
    delay: 0.2,
    opacity: 1,
    position: 'relative' as const,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

export const listVariants = {
  initial: {
    opacity: 0,
    y: -8,
  },
  exit: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
  },
};
